/**
 * @fileoverview Hook for managing project resources with optimistic updates.
 *
 * Provides CRUD operations for resources including:
 * - Adding single and batch resources
 * - Updating and deleting resources
 * - Fetching character portraits and main images
 */

'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { useProject } from '@/context/project-context';
import { useToast } from '@/hooks/use-toast';
import { Resource } from '@/domains/resource-management/schemas';
import { saveResource, deleteResource as deleteResourceOnServer, saveResourcesBatch, deleteResourcesBatch as deleteResourcesBatchOnServer, detachCharacterPortrait } from '../services/repository';
import { unlinkEntityFromResources, EntityType } from '../services/unlink-entity';
import { clearImportPersistedData } from './use-import-source';

export function useResources() {
  const { project, setProject, updateProjectCore, fetchAllResources: rehydrateProject } = useProject();
  const { toast } = useToast();
  
  const characters_v2 = useMemo(() => project.characters_v2 || [], [project.characters_v2]);


  const fetchAllResources = useCallback(() => {
    if (rehydrateProject) {
      rehydrateProject();
    }
  }, [rehydrateProject]);

  const addResource = useCallback(async (resourceData: Resource): Promise<Resource> => {
    let previousResources: Resource[] = [];
    setProject(prev => {
      previousResources = prev.resources || [];
      return { ...prev, resources: [...previousResources, resourceData], updatedAt: new Date().toISOString() };
    });

    try {
      await saveResource(project.id, resourceData);
    } catch (error) {
      setProject(prev => ({ ...prev, resources: previousResources }));
      toast({ variant: 'destructive', title: 'Save Failed', description: 'Could not save the new resource.' });
      throw error;
    }
    return resourceData;
  }, [project.id, setProject, toast]);

  const addManyResources = useCallback(async (resourcesData: Omit<Resource, 'id'>[]): Promise<Resource[]> => {
    const newResources: Resource[] = resourcesData.map(data => ({ id: crypto.randomUUID(), ...data }));
    let previousResources: Resource[] = [];

    setProject(prev => {
      previousResources = prev.resources || [];
      return { ...prev, resources: [...previousResources, ...newResources], updatedAt: new Date().toISOString() };
    });

    try {
        await saveResourcesBatch(project.id, newResources);
    } catch (error) {
        setProject(prev => ({ ...prev, resources: previousResources }));
        toast({ variant: 'destructive', title: 'Batch Save Failed', description: 'Could not save all new resources.' });
        throw error;
    }
    return newResources;
  }, [project.id, setProject, toast]);

  const updateResource = useCallback(async (resourceId: string, updates: Partial<Resource>) => {
    let updatedResource: Resource | undefined;
    let previousResources: Resource[] = [];

    setProject(prev => {
      previousResources = prev.resources || [];
      const resourceToUpdate = previousResources.find(r => r.id === resourceId);
      if (!resourceToUpdate) return prev;

      updatedResource = { ...resourceToUpdate, ...updates };
      return {
        ...prev,
        resources: previousResources.map(r => r.id === resourceId ? updatedResource! : r),
        updatedAt: new Date().toISOString(),
      };
    });

    if (!updatedResource) throw new Error(`Resource with ID ${resourceId} not found.`);

    try {
        await saveResource(project.id, updatedResource);
    } catch (error) {
        setProject(prev => ({ ...prev, resources: previousResources }));
        toast({ variant: 'destructive', title: 'Save Failed', description: 'Could not update resource.' });
        throw error;
    }
  }, [project.id, setProject, toast]);

  const deleteResource = useCallback(async (resourceId: string): Promise<void> => {
    const resourceToDelete = (project.resources || []).find(r => r.id === resourceId);
    if (!resourceToDelete) return;

    // Optimistic update using functional updater to avoid stale closure issues
    setProject(prev => ({
      ...prev,
      resources: (prev.resources || []).filter(r => r.id !== resourceId),
      characters_v2: (prev.characters_v2 || []).map(c =>
          c.mainImageId === resourceId ? { ...c, mainImageId: undefined } : c
      ),
      // Clear importSource and completed steps if the deleted resource was the active import source
      ...(prev.importSource?.resourceId === resourceId ? { importSource: null, importCompletedSteps: [] } : {}),
      updatedAt: new Date().toISOString()
    }));

    const wasImportSource = project.importSource?.resourceId === resourceId;

    try {
        // Delete the resource document and the file in storage
        await deleteResourceOnServer(project.id, resourceToDelete);

        // Clean up any character references on the server
        await detachCharacterPortrait(project.id, resourceId);
    } catch (error) {
        // Rollback: re-add the resource using functional updater
        setProject(prev => ({
            ...prev,
            resources: [...(prev.resources || []), resourceToDelete],
            ...(wasImportSource ? { importSource: project.importSource, importCompletedSteps: project.importCompletedSteps } : {}),
            updatedAt: new Date().toISOString()
        }));
        toast({ variant: "destructive", title: "Deletion Failed", description: `Could not remove "${resourceToDelete.fileName}".` });
        throw error;
    }

    // Clear all import data (importSource, completed steps, localStorage text)
    // separately so a failure here cannot roll back the already-completed resource deletion.
    if (wasImportSource) {
      try {
        await clearImportPersistedData(project.id, updateProjectCore);
      } catch (error) {
        console.error('Failed to clear import data:', error);
      }
    }
  }, [project, setProject, updateProjectCore, toast]);
  
  const deleteResourcesBatch = useCallback(async (resourcesToDelete: Resource[]): Promise<void> => {
    if (resourcesToDelete.length === 0) return;

    const resourceIdsToDelete = new Set(resourcesToDelete.map(r => r.id));
    let previousResources: Resource[] = [];

    setProject(prev => {
      previousResources = prev.resources || [];
      return {
        ...prev,
        resources: previousResources.filter(r => !resourceIdsToDelete.has(r.id)),
        // Clear importSource and completed steps if the deleted batch includes the active import source
        ...(prev.importSource?.resourceId && resourceIdsToDelete.has(prev.importSource.resourceId) ? { importSource: null, importCompletedSteps: [] } : {}),
        updatedAt: new Date().toISOString(),
      };
    });

    const wasImportSource = project.importSource?.resourceId && resourceIdsToDelete.has(project.importSource.resourceId);

    try {
      await deleteResourcesBatchOnServer(project.id, resourcesToDelete);
    } catch (error) {
      setProject(prev => ({
        ...prev,
        resources: previousResources,
        ...(wasImportSource ? { importSource: project.importSource, importCompletedSteps: project.importCompletedSteps } : {}),
      }));
      toast({ variant: 'destructive', title: 'Batch Deletion Failed', description: 'Could not delete the resources.' });
      throw error;
    }

    // Clear all import data (importSource, completed steps, localStorage text)
    // separately so a failure here cannot roll back the already-completed batch deletion.
    if (wasImportSource) {
      try {
        await clearImportPersistedData(project.id, updateProjectCore);
      } catch (error) {
        console.error('Failed to clear import data:', error);
      }
    }
  }, [project.id, project.importSource, project.importCompletedSteps, setProject, updateProjectCore, toast]);

  const getCharacterPortraits = useCallback((characterId: string): Resource[] => {
      return (project.resources || []).filter(r => r.characterIds?.includes(characterId) && r.fileType?.startsWith('image/'));
  }, [project.resources]);

  const getMainImageForCharacter = useCallback((characterId: string): Resource | undefined => {
      const character = characters_v2.find(c => c.id === characterId);
      if (!character || !character.mainImageId) return undefined;
      return (project.resources || []).find(r => r.id === character.mainImageId);
  }, [characters_v2, project.resources]);

  const getWorldObjectImages = useCallback((worldObjectId: string): Resource[] => {
      return (project.resources || []).filter(r => r.worldElementIds?.includes(worldObjectId) && r.fileType?.startsWith('image/'));
  }, [project.resources]);

  /**
   * Unlinks an entity from its resources and deletes any orphaned resources.
   * Returns the IDs of resources that were deleted (orphans).
   */
  const unlinkEntityAndCleanup = useCallback(async (
    entityId: string,
    entityType: EntityType,
  ): Promise<string[]> => {
    const field = entityType === 'character' ? 'characterIds' : 'worldElementIds';

    // Optimistic update: remove entityId from resource arrays, track orphans
    let orphanIds: Set<string> = new Set();
    const allCharacters = project.characters_v2 || [];
    const we = project.worldElements_v3;
    const allMainImageIds = new Set<string>();

    // Collect all mainImageIds for orphan detection (excluding the entity being deleted)
    allCharacters
      .filter(c => c.id !== entityId)
      .forEach(c => { if (c.mainImageId) allMainImageIds.add(c.mainImageId); });

    if (we) {
      for (const typeKey of ['objects', 'places', 'sets', 'events', 'organizations', 'species', 'systems'] as const) {
        ((we[typeKey] || []) as { id: string; mainImageId?: string }[])
          .filter(el => el.id !== entityId)
          .forEach(el => { if (el.mainImageId) allMainImageIds.add(el.mainImageId); });
      }
    }

    setProject(prev => {
      const resources = prev.resources || [];
      const updated = resources.map(r => {
        const ids: string[] | undefined = r[field] as string[] | undefined;
        if (!ids?.includes(entityId)) return r;
        return { ...r, [field]: ids.filter(id => id !== entityId) };
      });

      // Identify orphans after unlinking
      orphanIds = new Set(
        updated
          .filter(r =>
            (!r.characterIds || r.characterIds.length === 0) &&
            (!r.worldElementIds || r.worldElementIds.length === 0) &&
            !allMainImageIds.has(r.id)
          )
          .filter(r => {
            // Only consider resources that were actually linked to this entity
            const original = resources.find(o => o.id === r.id);
            const originalIds: string[] | undefined = original?.[field] as string[] | undefined;
            return originalIds?.includes(entityId);
          })
          .map(r => r.id)
      );

      return {
        ...prev,
        resources: updated.filter(r => !orphanIds.has(r.id)),
        updatedAt: new Date().toISOString(),
      };
    });

    // Server-side: unlink and get authoritative orphan list
    const { orphanedResources } = await unlinkEntityFromResources(
      project.id,
      entityId,
      entityType,
    );

    // Delete orphaned resources from storage + Firestore
    if (orphanedResources.length > 0) {
      await deleteResourcesBatchOnServer(project.id, orphanedResources);
    }

    return orphanedResources.map(r => r.id);
  }, [project.id, project.characters_v2, project.worldElements_v3, setProject]);

  return {
    resources: project.resources || [],
    addResource,
    addManyResources,
    updateResource,
    deleteResource,
    deleteResourcesBatch,
    getCharacterPortraits,
    getWorldObjectImages,
    fetchAllResources,
    getMainImageForCharacter,
    unlinkEntityAndCleanup,
  };
}
